import { Vector3 } from 'three';
import { getVerticesFromLocation } from '../../../../../render/mesh/cloud/point-cloud-utils';
import {
  getInProgressCuboidLocation,
  getVisibleFocusedCuboidLocation,
} from '../../../../../state/focused-shape-utils';
import { PointCloudContentState } from '../../../../../state/point-cloud-content-state';
import { CloudShaderMaterial } from '../../../cloud-shader-material';

export type CuboidVertices = [Vector3, Vector3, Vector3, Vector3];

export interface InsideCuboidModeUniforms {
  cuboidVertices: CuboidVertices;
}

export const insideCuboidModeUniforms = `
  struct InsideCuboidModeUniforms {
    vec3[4] cuboidVertices;
  };
`;

// Note: math to determine if we are inside or outside a cuboid comes from:
// https://math.stackexchange.com/questions/1472049/check-if-a-point-is-inside-a-rectangular-shaped-area-3d
export const getColorForInsideCuboidMode = `
  bool isInsideCuboid() {
    vec3[4] cuboidVertices = color.insideCuboid.cuboidVertices;

    vec3 p14 = vec3(cuboidVertices[0] - cuboidVertices[2]);
    vec3 p12 = vec3(cuboidVertices[0] - cuboidVertices[1]);
    vec3 p15 = vec3(cuboidVertices[0] - cuboidVertices[3]);

    vec3 u = cross(p14,p15);
    vec3 v = cross(p12,p15);
    vec3 w = cross(p12,p14);

    float x = dot(position, u);
    float y = dot(position, v);
    float z = dot(position, w);

    bool isX = min(dot(u, cuboidVertices[0]), dot(u, cuboidVertices[1])) < x  && x < max(dot(u, cuboidVertices[0]), dot(u, cuboidVertices[1]));
    bool isY = min(dot(v, cuboidVertices[0]), dot(v, cuboidVertices[2])) < y && y < max(dot(v, cuboidVertices[0]), dot(v, cuboidVertices[2]));
    bool isZ = min(dot(w, cuboidVertices[0]), dot(w, cuboidVertices[3])) < z && z < max(dot(w, cuboidVertices[0]), dot(w, cuboidVertices[3]));
    return isX && isY && isZ;
  }

  vec4 getColorForInsideCuboidMode(vec4 outsideColor) {
    if(isInsideCuboid()){
      if(instanceId == CLOUD_INSTANCE_ID) {
        return vec4(1.0, 1.0, 0.0, 1.0);
      } else {
        return vec4(0.827, 0.451, 0.647, 1.0);
      }
    } else {
      return outsideColor;
    }
  }
`;

export function setInsideCuboidModeUniforms(
  material: CloudShaderMaterial,
  state?: PointCloudContentState,
) {
  if (!state) {
    material.setColorUniform('insideCuboid', {
      cuboidVertices: defaultCuboidVertices,
    });
    return;
  }

  let location = getVisibleFocusedCuboidLocation(state);
  if (!location) {
    location = getInProgressCuboidLocation(state);
  }

  material.setColorUniform('insideCuboid', {
    cuboidVertices: getVerticesFromLocation(location),
  });
}

const defaultCuboidVertices: CuboidVertices = [
  new Vector3(),
  new Vector3(),
  new Vector3(),
  new Vector3(),
];
